import formatPropertyName from "utils/column/formatPropertyName";

import { useColumns } from "hooks";

import { IGroupByListColumn } from "models";

import {
  useIsShapefileFieldAbsentFromProject,
  useResolveShapefileColumn
} from "components/project/shapefiles/hooks/useResolveShapefileColumn";
import { useUserDefaults } from "components/user/hooks";

import { useFacilityColumns } from "../../../hooks/useColumns";
import { EntityKind } from "../../../models/entityKind";

export interface FocusTileColumn {
  /** The stored tile string, forecast qualification intact. */
  tile: string;
  /** The base column it resolves to. */
  column: IGroupByListColumn;
}

const useTileColumns = (entityKind: EntityKind) => {
  const columns = useColumns();
  const facilityColumns = useFacilityColumns();
  const resolveShapefileColumn = useResolveShapefileColumn();
  const isAbsentShapefileField = useIsShapefileFieldAbsentFromProject();
  const defaults = useUserDefaults();

  const tiles =
    entityKind == EntityKind.Well
      ? defaults?.focusTiles?.tiles ?? []
      : defaults?.facilityFocusTiles?.tiles ?? [];

  const getColumnsByEntityKind = () =>
    entityKind === EntityKind.Well ? columns : facilityColumns;

  const tileColumns = tiles?.reduce((list, tile) => {
    const propertyName = formatPropertyName(tile);
    // Absence outranks the live column list, mirroring the settings screens:
    // picking a shapefile in group-by materializes its columns into the
    // column list and a project switch does not evict them, so a stale entry
    // could otherwise render a live focus tile for a field the settings
    // screen calls unavailable (EVA-126).
    const column = isAbsentShapefileField(propertyName)
      ? null
      : getColumnsByEntityKind().find((c) => propertyName === c.property) ??
        resolveShapefileColumn(propertyName);
    if (!column) return list;
    list.push({ tile, column });
    return list;
  }, [] as FocusTileColumn[]);

  // Gate on the real column list, not tileColumns: a shapefile tile can resolve
  // (via its own async query) before the well/facility columns finish loading,
  // which would otherwise render the shapefile tile alone and then pop in the
  // rest a moment later.
  const isLoading = getColumnsByEntityKind().length === 0;

  return {
    isLoading,
    tileColumns
  };
};

export default useTileColumns;
